import React, { useState, useEffect, useContext } from "react";
import axiosClient from "../api/axiosClient";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";
import "../styles/AllTasks.css";

function MyTeam() {
  const { user } = useContext(AuthContext);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Modal states for Add Team Member
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({
    username: "",
    email: "",
    role: "Employee",
    manager_id: "",
  });

  // Fetch team members
  const fetchTeam = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosClient.get("/employees/my_team");
      setTeam(res.data.data || []);
    } catch (err) {
      console.error("Error fetching team:", err);
      setError("Unable to load team members. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  // Handle Add Team Member form submission
  const handleAddMember = async () => {
    if (!newMember.username || !newMember.email) {
      alert("Username and Email are required.");
      return;
    }

    try {
      const res = await axiosClient.post("/employees/add", newMember);
      alert(res.data.message || "Team member added successfully!");
      setShowAddModal(false);
      setNewMember({ username: "", email: "", role: "Employee", manager_id: "" });
      fetchTeam();
    } catch (err) {
      console.error("Error adding member:", err);
      const message =
        err.response?.data?.message || "Failed to add team member.";
      alert(`❌ ${message}`);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewMember({ username: "", email: "", role: "Employee", manager_id: "" });
  };

  // Role badge color
  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "badge bg-danger";
      case "manager":
        return "badge bg-primary";
      default:
        return "badge bg-secondary";
    }
  };

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-primary">👥 My Team</h3>
        <div>
          <button
            className="btn btn-outline-primary me-2"
            onClick={fetchTeam}
          >
            🔁 Refresh
          </button>
          <button
            className="btn btn-success"
            onClick={() => setShowAddModal(true)}
          >
            ➕ Add Team Member
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Loading team members...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : team.length === 0 ? (
        <div className="text-center text-muted">No team members found.</div>
      ) : (
        <div className="row g-4">
          {team.map((member, index) => (
            <motion.div
              key={index}
              className="col-md-6 col-lg-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              whileHover={{ scale: 1.03 }}
            >
              <div className="task-card shadow-sm p-3 border rounded-3 bg-white">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h5 className="fw-bold mb-0 text-dark">{member.username}</h5>
                  <span className={getRoleColor(member.role)}>{member.role}</span>
                </div>
                <p className="text-muted small mb-1">
                  <strong>Email:</strong> {member.email}
                </p>
                <p className="text-muted small mb-1">
                  <strong>Reporting To:</strong>{" "}
                  {member.manager || "—"}
                </p>
                <p className="text-muted small mb-0">
                  <strong>Status:</strong>{" "}
                  {member.is_active ? (
                    <span className="text-success">Active</span>
                  ) : (
                    <span className="text-danger">Inactive</span>
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ✅ Add Team Member Modal */}
      {showAddModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-primary">➕ Add Team Member</h5>
                <button type="button" className="btn-close" onClick={closeAddModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newMember.username}
                    onChange={(e) =>
                      setNewMember({ ...newMember, username: e.target.value })
                    }
                    placeholder="Enter username"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={newMember.email}
                    onChange={(e) =>
                      setNewMember({ ...newMember, email: e.target.value })
                    }
                    placeholder="Enter email address"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={newMember.role}
                    onChange={(e) =>
                      setNewMember({ ...newMember, role: e.target.value })
                    }
                  >
                    <option>Employee</option>
                    <option>Manager</option>
                    <option>Admin</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Reporting To (Optional)</label>
                  <select
                    className="form-select"
                    value={newMember.manager_id}
                    onChange={(e) =>
                      setNewMember({ ...newMember, manager_id: e.target.value })
                    }
                  >
                    <option value="">Select Manager</option>
                    {team.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.username} ({m.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAddModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleAddMember}>
                  Add Member
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={closeAddModal}
          ></div>
        </div>
      )}
    </div>
  );
}

export default MyTeam;
